import { utcToZonedTime } from 'date-fns-tz';
import Highcharts, { Options as HighchartsOptions, SeriesLineOptions } from 'highcharts';
import HighchartsBoost from 'highcharts/modules/boost';
import HighchartsReact from 'highcharts-react-official';
import { TFunction } from 'i18next';
import React, { memo } from 'react';

import { BENCHMARK_SLUG, staticSLAChartOptions } from './config';
import { slaTooltipFormatter } from './utils';
import { useAuth } from '../../../../app/AuthorizationProvider';
import { EstimatedServiceLevelPerQueue } from '../../../../graphql/components';
import { RecordBooleanType } from '../../../../types';
import { getZonedDateTimezoneOffset } from '../../../../utils/date';
import { MIN_15 } from '../../config';
import { MissingForecastPerQueue, QueuesWithColors, SchedulingChartProps as ChartProps } from '../../types';

HighchartsBoost(Highcharts);

/* eslint-disable react/no-unused-prop-types */
export interface ServiceLevelAgreementChartProps extends Pick<ChartProps, 'yAxisTicks'> {
  currentHoveredQueueId?: string;
  dataPerQueueVisibility: RecordBooleanType;
  end: number;
  estimatedServiceLevelPerQueue?: Record<string, EstimatedServiceLevelPerQueue>;
  interval?: number;
  isLoadingData: boolean;
  isMultiQueueSchedule: boolean;
  missingForecastIntervalsPerQueue: MissingForecastPerQueue;
  queueIds: string[];
  queues: QueuesWithColors;
  setCurrentHoveredQueueId: React.Dispatch<React.SetStateAction<string | undefined>>;
  start: number;
  t: TFunction;
  timezone: string;
  width: number;
}
/* eslint-enable react/no-unused-prop-types */

const generateChartConfig: (
  props: ServiceLevelAgreementChartProps,
  locale: string,
  localeObj?: Locale
) => HighchartsOptions = (
  {
    currentHoveredQueueId,
    dataPerQueueVisibility,
    end,
    estimatedServiceLevelPerQueue,
    interval = MIN_15,
    isLoadingData,
    isMultiQueueSchedule,
    queueIds,
    queues,
    setCurrentHoveredQueueId,
    start,
    t,
    timezone,
    yAxisTicks,
    width,
  },
  locale,
  localeObj
) => {
  const showBenchmarkSeriesPermanently =
    !isMultiQueueSchedule || Object.values(dataPerQueueVisibility).filter(Boolean).length === 1;

  return {
    ...staticSLAChartOptions,
    chart: {
      ...staticSLAChartOptions.chart,
      width,
    },
    series: isLoadingData
      ? []
      : queueIds.reduce((series: SeriesLineOptions[], queueId) => {
          const { color } = queues[queueId];
          const defaultSeriesOptions = (staticSLAChartOptions.series as SeriesLineOptions[])[0];

          const currentSeriesData = [...(estimatedServiceLevelPerQueue?.[queueId]?.series.current ?? [])];
          const currentSeriesLastDataOutOfRange = currentSeriesData.pop();

          const benchmarkSeriesData = [...(estimatedServiceLevelPerQueue?.[queueId]?.series.benchmark ?? [])];
          const benchmarkSeriesLastDataOutOfRange = benchmarkSeriesData.pop();

          series.push({
            ...defaultSeriesOptions,
            // adds 0.5 opacity
            color: currentHoveredQueueId && currentHoveredQueueId !== queueId ? `${color}80` : color,
            data: [
              ...currentSeriesData,
              {
                x: currentSeriesLastDataOutOfRange?.[0] ?? end,
                y: currentSeriesLastDataOutOfRange?.[1] ?? null,
                marker: {
                  states: {
                    hover: {
                      enabled: false,
                    },
                  },
                },
              },
            ],
            events: showBenchmarkSeriesPermanently
              ? {
                  mouseOver: () => null,
                  mouseOut: () => null,
                }
              : {
                  mouseOver: () => setCurrentHoveredQueueId(queueId),
                  mouseOut: () => setCurrentHoveredQueueId(undefined),
                },

            id: `${queueId}__current`,
            marker: {
              enabled: false,
            },
            name: isMultiQueueSchedule ? queues[queueId].name : t('intraday.charts.legendItems.current'),
            states: showBenchmarkSeriesPermanently
              ? defaultSeriesOptions.states
              : {
                  inactive: {
                    opacity: 1,
                  },
                  hover: {
                    ...defaultSeriesOptions.states?.hover,
                    lineWidthPlus: 0,
                    enabled:
                      showBenchmarkSeriesPermanently || (!!currentHoveredQueueId && currentHoveredQueueId === queueId),
                  },
                },
            type: 'line',
            visible: dataPerQueueVisibility[queueId],
            zIndex: currentHoveredQueueId && currentHoveredQueueId === queueId ? 1 : undefined, // moving currently hovered series to front, so that once user moves back and forth on the x-axis over the current series, the hover is not moved to any other series having points nearby
          });

          series.push({
            ...defaultSeriesOptions,
            color: '#818083',
            data: [
              ...benchmarkSeriesData,
              {
                x: benchmarkSeriesLastDataOutOfRange?.[0] ?? end,
                y: benchmarkSeriesLastDataOutOfRange?.[1] ?? null,
                marker: {
                  states: {
                    hover: {
                      enabled: false,
                    },
                  },
                },
              },
            ],
            id: `${queueId}${BENCHMARK_SLUG}`,
            marker: {
              enabled: false,
            },
            name: t('intraday.charts.legendItems.benchmark'),
            states: {
              inactive: {
                opacity: 1,
              },
              hover: {
                ...defaultSeriesOptions.states?.hover,
                lineWidthPlus: 0,
                enabled: showBenchmarkSeriesPermanently,
              },
            },
            type: 'line',
            visible:
              dataPerQueueVisibility[queueId] && (currentHoveredQueueId === queueId || showBenchmarkSeriesPermanently),
          });

          return series;
        }, []),
    time: {
      getTimezoneOffset: (timestamp: number) =>
        getZonedDateTimezoneOffset(utcToZonedTime(timestamp, timezone), timezone),
      useUTC: true,
    },
    tooltip: {
      ...staticSLAChartOptions.tooltip,
      formatter: slaTooltipFormatter(t, interval, timezone, locale, end, localeObj, currentHoveredQueueId),
    },
    xAxis: {
      ...staticSLAChartOptions.xAxis,
      tickInterval: interval,
      min: start,
      max: end,
      endOnTick: false,
    },
    yAxis: {
      ...staticSLAChartOptions.yAxis,
      min: yAxisTicks[0],
      max: yAxisTicks[yAxisTicks.length - 1] + 0.05, // to prevent clipping of marker in case of data includes 100% values
      tickAmount: 5,
      tickInterval: yAxisTicks[1] - yAxisTicks[0],
    },
  };
};

export const ServiceLevelAgreementChartComponent: React.FC<ServiceLevelAgreementChartProps> = (props) => {
  const { locale, localeObj } = useAuth();

  const { dataPerQueueVisibility, isMultiQueueSchedule, setCurrentHoveredQueueId, width } = props;

  return (
    <div
      style={{ width }}
      data-test-id="scheduling-composer:scheduling-chart:sla"
      onMouseLeave={() => {
        if (isMultiQueueSchedule && Object.values(dataPerQueueVisibility).filter(Boolean).length > 1) {
          setCurrentHoveredQueueId(undefined);
        }
      }}
    >
      <HighchartsReact highcharts={Highcharts} options={generateChartConfig(props, locale, localeObj)} />
    </div>
  );
};

export const ServiceLevelAgreementChart = memo(ServiceLevelAgreementChartComponent);
